/**
 * Phase 6 — cross-tab sync signal via `BroadcastChannel`.
 *
 * When one tab writes to GitHub (via `add()` / `remove()` / `sync()` on
 * `useAssetLocker` or `useCaptures`), other tabs should reconcile without
 * waiting for the next polling tick (15 s — see `use-github-sync.ts`).
 * We post a tiny `{ type: "sync" }` message on a single shared channel;
 * the polling hook listens and re-invalidates its TanStack Query key,
 * which kicks the `Promise.all` over both stores' `sync()` actions.
 *
 * The channel is created lazily — `BroadcastChannel` isn't available in
 * older Safari or in jsdom (so the smoke tests bail cleanly), and we
 * don't want a module-load throw in either case.
 *
 * Why this is a separate file: the import would otherwise be a cycle
 * (`stores/useAssetLocker` ← `lib/sync-broadcast` ← `lib/use-github-sync`
 * ← `stores/useAssetLocker`). Keeping the postMessage helper and the
 * subscribe helper colocated here lets the stores broadcast WITHOUT
 * pulling the TanStack Query layer in.
 *
 * See `docs/adr/0010-github-persistence.md` → "Migration path" → step 3
 * for the rationale. The channel name (`"xaes-uploads"`) is fixed by
 * that ADR.
 */

const CHANNEL_NAME = "xaes-uploads";

/**
 * Lazily-constructed module-singleton channel. Both `broadcastSync()`
 * and `onCrossTabSync()` write through the same instance so a tab can
 * post + listen on the same channel without double-subscribing.
 */
let channel: BroadcastChannel | null = null;

/**
 * True when the current runtime has the `BroadcastChannel` global.
 * Centralised so the stores and the hook don't each have to guard
 * `typeof window` / `"BroadcastChannel" in window` themselves.
 */
function isSupported(): boolean {
  // The truthy check on `window.BroadcastChannel` covers both the
  // "key not present" case (older Safari, SSR) and the "key present
  // but value stubbed to undefined" case (vitest globals — see
  // `sync-broadcast.test.ts`).
  return (
    typeof window !== "undefined" &&
    "BroadcastChannel" in window &&
    typeof (window as unknown as { BroadcastChannel?: unknown })
      .BroadcastChannel === "function"
  );
}

/**
 * Post a `{ type: "sync" }` ping to every other tab. No-op when the
 * runtime doesn't have `BroadcastChannel`. Safe to call from any code
 * path — store `add()` / `remove()` / `sync()` mirror writes all
 * funnel through here.
 *
 * The timestamp is informational; the consumer doesn't read it today,
 * but logging it on the receiving side made the cross-tab handshake
 * easy to verify in DevTools during Phase 6 development.
 */
export function broadcastSync(): void {
  if (!isSupported()) return;
  // The `??=` shorthand keeps the channel a module-singleton without a
  // separate init step. Failing here (e.g. quota / blocked origin) is
  // still a soft miss — the polling tick will catch the divergence.
  channel ??= new BroadcastChannel(CHANNEL_NAME);
  try {
    channel.postMessage({ type: "sync", timestamp: Date.now() });
  } catch {
    /* `postMessage` throws on closed channels and on Firefox when the
       structured-clone payload trips serialisation. Either way the
       caller is fire-and-forget; the next sync tick will catch us up. */
  }
}

/**
 * Subscribe to cross-tab sync pings. The handler runs whenever another
 * tab calls {@link broadcastSync}. Returns a cleanup function — call it
 * from a `useEffect` return path so React unmounts don't leak listeners.
 *
 * Returns a no-op cleanup when `BroadcastChannel` isn't available; the
 * caller can wire this up unconditionally without a feature-check at
 * the call site.
 */
export function onCrossTabSync(handler: () => void): () => void {
  if (!isSupported()) return () => {};
  channel ??= new BroadcastChannel(CHANNEL_NAME);
  // Filter on `type === "sync"` so a future message shape (e.g. a
  // future "presence" ping for multi-facilitator awareness) doesn't
  // accidentally trigger a reconcile.
  function listener(event: MessageEvent): void {
    if (
      event.data &&
      typeof event.data === "object" &&
      (event.data as { type?: unknown }).type === "sync"
    ) {
      handler();
    }
  }
  channel.addEventListener("message", listener);
  return () => {
    // `channel` is captured in closure but could in theory be reassigned
    // by a sibling caller — read it through the module ref so the
    // removeEventListener targets the currently-active instance.
    channel?.removeEventListener("message", listener);
  };
}

/**
 * Test-only escape hatch. Drops the cached channel so a follow-up test
 * can re-stub `globalThis.BroadcastChannel` and observe a fresh init.
 * Not exported from any production code path.
 */
export function _resetChannelForTests(): void {
  try {
    channel?.close();
  } catch {
    /* noop */
  }
  channel = null;
}
